'use client';

import * as React from 'react';

import { cn } from '~/lib/utils';
import {
  type BrailleLoaderSpeed,
  type BrailleLoaderVariant,
  generateFrames,
  getVariantGridSize,
  normalizeVariant,
} from '~/lib/braille-loader';

type BrailleLoaderProps = React.ComponentProps<'span'> & {
  variant?: BrailleLoaderVariant;
  speed?: BrailleLoaderSpeed;
  /** Multiplier applied on top of `speed` (e.g. 1.5 = 50% faster). */
  speedScale?: number;
  label?: string;
  showLabel?: boolean;
  fontSize?: number;
};

const BLANK_BRAILLE_PATTERN = /\u2800+$/u;

function trimTrailingBlankBraille(text: string) {
  return text.replace(BLANK_BRAILLE_PATTERN, '');
}

const speedMultiplier: Record<BrailleLoaderSpeed, number> = {
  slow: 1.5,
  normal: 1,
  fast: 0.6,
};

function getPrefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    'matchMedia' in window &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function usePrefersReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = React.useState(getPrefersReducedMotion);

  React.useEffect(() => {
    if (!('matchMedia' in window)) return;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = () => setPrefersReducedMotion(mediaQuery.matches);

    handleChange();
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  return prefersReducedMotion;
}

function BrailleLoader({
  variant = 'breathe',
  speed = 'normal',
  speedScale = 1,
  className,
  label = 'Loading',
  showLabel = false,
  fontSize = 28,
  style,
  ...props
}: BrailleLoaderProps) {
  const resolvedVariant = normalizeVariant(variant);
  const [width, height] = getVariantGridSize(resolvedVariant);
  const spanRef = React.useRef<HTMLSpanElement>(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  const framesData = React.useMemo(() => {
    return generateFrames(resolvedVariant, width, height);
  }, [resolvedVariant, width, height]);

  React.useEffect(() => {
    if (prefersReducedMotion || !spanRef.current) return;

    const frames = framesData.frames;
    let frameIndex = 0;
    const baseInterval = framesData.interval;
    const interval = (baseInterval * speedMultiplier[speed]) / speedScale;

    const updateFrame = () => {
      if (spanRef.current) {
        spanRef.current.textContent = trimTrailingBlankBraille(frames[frameIndex] ?? '');
      }
      frameIndex = (frameIndex + 1) % frames.length;
    };

    updateFrame();
    const intervalId = setInterval(updateFrame, interval);

    return () => {
      clearInterval(intervalId);
    };
  }, [framesData, prefersReducedMotion, speed, speedScale]);

  return (
    <span
      role="status"
      aria-live="polite"
      className={cn(
        'inline-flex shrink-0 items-center justify-center overflow-hidden leading-none',
        showLabel && 'gap-1',
        className,
      )}
      aria-label={showLabel ? undefined : label}
      {...props}
      style={{
        width: showLabel ? undefined : fontSize,
        height: fontSize,
        ...style,
      }}
    >
      <span
        ref={spanRef}
        aria-hidden="true"
        className="block size-[1em] overflow-hidden text-center"
        style={{
          fontFamily: 'monospace',
          whiteSpace: 'pre',
          fontSize: `${fontSize}px`,
          lineHeight: 1,
          letterSpacing: 0,
        }}
      >
        {trimTrailingBlankBraille(framesData.frames[0] ?? '')}
      </span>
      {showLabel ? (
        <span className="text-muted-foreground text-sm leading-none">{label}</span>
      ) : null}
    </span>
  );
}

export { BrailleLoader, type BrailleLoaderProps };
